import type { ZodType, ZodTypeDef } from "zod";
import { z } from "zod";
import type { ProviderRegistry } from "./provider";

// §3.4/D101 Model-System — pro Tool wählbare „Modelle" (Dropdown). Ein Modell ist ein
// DEKLARATIVES Profil über dem Settings-Schema: eigene Defaults, versteckte Felder und
// Provider-Bindungen je Slot. Die Helfer hier sind die EINZIGE Logik des Systems —
// Submit, Credits-Vorschau und Frontend teilen sie (D12-SSOT, kein Parallel-Code):
//   • modelEnum(models)      — `model`-Settings-Feld AUS der Liste abgeleitet (Single Source)
//   • selectModel(models, raw) — Pass 1 des two-pass-Bounds (liest NUR raw.model)
//   • withModelGating(base, models) — preprocess: hidden strippen + Defaults seeden
//   • resolveModelProvider(providers, model, slot) — Bounds UND Build lösen identisch auf (D34)

/**
 * Deklaratives Modell-Profil (D101). `models[0]` = Default-Modell (Konvention wie der
 * Provider-Default „erster registrierte"). Tools ohne `models` verhalten sich wie bisher.
 */
export interface ToolModelDef<TSettings = unknown> {
  /** stabil — persistiert als `settings.model` in Jobs (D32: nie umbenennen, nur ersetzen). */
  id: string;
  /** Dropdown-Anzeige (ModelPicker, §11). */
  label: string;
  description?: string;
  /** Default-Overrides: seeden NUR FEHLENDE Felder vor dem Parse (explizite Werte gewinnen). */
  defaults?: Partial<TSettings>;
  /**
   * Im Modell NICHT verfügbare Felder: die UI blendet sie aus, der Server STRIPPT jeden
   * eingehenden Wert und erzwingt den Modell- bzw. Schema-Default (strip statt 400 —
   * Re-Submit alter Jobs mit jetzt-verstecktem Feld bleibt grün, D32; ein verstecktes
   * Feld kann NIE Build/Pricing beeinflussen).
   * Typ: key-typisiert für konkrete Settings, aber `string[]` bei `unknown` — sonst wäre
   * `ToolModelDef<X>` nicht `ToolModelDef<unknown>` zuweisbar (Registry arbeitet mit
   * `ToolCore<unknown>`; `keyof unknown = never` würde die Zuweisung varianz-brechen).
   */
  hiddenSettings?: readonly (TSettings extends Record<string, unknown> ? keyof TSettings & string : string)[];
  /** slot → providerId; fehlt ⇒ Slot-Default. Auflösung NUR über `resolveModelProvider`. */
  providerBindings?: Readonly<Record<string, string>>;
}

/**
 * `model`-Enum AUS der models-Liste (Single Source, D101) — `.default(models[0].id)`.
 * Explizite, NICHT-generische Rückgabe: bricht die Typ-Zirkularität models ↔ Settings-Schema
 * (das Schema referenziert die Liste als WERT, die Liste den Settings-Typ als TYP).
 */
export function modelEnum<T>(models: readonly ToolModelDef<T>[]): z.ZodDefault<z.ZodEnum<[string, ...string[]]>> {
  const ids = models.map((m) => m.id);
  const first = ids[0];
  if (first === undefined) throw new Error("modelEnum: models-Liste darf nicht leer sein (D101).");
  if (new Set(ids).size !== ids.length) throw new Error(`modelEnum: doppelte Modell-ids (${ids.join(", ")}) — D101.`);
  return z.enum([first, ...ids.slice(1)]).default(first);
}

/**
 * Pass 1 des two-pass-Bounds (D101): liest AUSSCHLIESSLICH `rawSettings.model`.
 * Fehlend/unbekannt/kein Objekt → Default-Modell `models[0]` (deckt alte Jobs ohne
 * `model`-Feld ab; ein UNGÜLTIGER String failt anschließend laut am enum-Parse).
 */
export function selectModel<T>(models: readonly ToolModelDef<T>[], rawSettings: unknown): ToolModelDef<T> {
  const first = models[0];
  if (!first) throw new Error("selectModel: models-Liste darf nicht leer sein (D101).");
  if (typeof rawSettings === "object" && rawSettings !== null && !Array.isArray(rawSettings)) {
    const id = (rawSettings as Record<string, unknown>)["model"];
    if (typeof id === "string") {
      const hit = models.find((m) => m.id === id);
      if (hit) return hit;
    }
  }
  return first;
}

/**
 * Modell-Gating als `z.preprocess` VOR dem Basis-Schema (D101):
 *   (a) `hiddenSettings`: eingehende Werte werden VERWORFEN und durch den Modell- bzw.
 *       Schema-Default ersetzt — ein verstecktes Feld kann nie Build/Pricing beeinflussen;
 *       fehlt beides, failt der Parse LAUT (fail-closed Konfigurationsfehler).
 *   (b) `defaults`: seeden nur FEHLENDE Felder (explizite User-Werte gewinnen).
 * Nicht-Objekte laufen unverändert ins Basis-Schema (dessen Objekt-Fehler greift normal).
 * Generisch doppelt (TSettings/TModel), damit Modell-Listen über einem TEIL-Feldtyp
 * deklariert werden können (nur die Felder, die Modelle anfassen — bricht Typ-Zyklen).
 */
export function withModelGating<TSettings, TModel>(
  base: ZodType<TSettings, ZodTypeDef, unknown>,
  models: readonly ToolModelDef<TModel>[],
): ZodType<TSettings, ZodTypeDef, unknown> {
  return z.preprocess((raw) => {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return raw;
    const model = selectModel(models, raw);
    const out: Record<string, unknown> = { ...(raw as Record<string, unknown>) };
    const defaults = (model.defaults ?? {}) as Record<string, unknown>;
    for (const key of model.hiddenSettings ?? []) {
      delete out[key];
      const d = defaults[key];
      if (d !== undefined) out[key] = d;
    }
    for (const [key, d] of Object.entries(defaults)) {
      if (out[key] === undefined && d !== undefined) out[key] = d;
    }
    return out;
  }, base);
}

/**
 * Modell-gebundener Provider eines Slots (D101): `providerBindings[slot]` ODER Slot-Default.
 * Der EINE Helfer für `boundSettingsSchema` UND `buildPipeline` — erzwingt konstruktiv,
 * dass Bounds, Preis (gepinnter `providerId`-Param) und Ausführung (resolvePinned) auf
 * DEMSELBEN Adapter laufen (D34).
 */
export function resolveModelProvider<P, T = unknown>(
  providers: ProviderRegistry,
  model: ToolModelDef<T> | undefined,
  slot: string,
): P {
  return providers.resolve<P>(slot, model?.providerBindings?.[slot]);
}
